import { createPanelTemplate, inferTemplateIdFromPanelCount } from "../comic/PanelTemplates";
import { isPanelGeometryValid } from "../comic/PanelGeometry";
import {
  COMIC_DOCUMENT_SCHEMA_VERSION,
  isComicTemplateId,
  type ComicBubbleObject,
  type ComicPanel,
  type ComicTemplateId,
  type PersistedDocumentState,
} from "../comic/comicTypes";
import { cloneDocumentState, validatePersistedDocumentState } from "../comic/comicValidation";
import { PROJECT_SCHEMA_VERSION } from "./createProject";
import type { PerfectDrawLayer, PerfectDrawProject } from "./types";
import { defaultLayerName, sanitizeLayerName } from "../layers/layerNames";
import { normalizeLayerOrder, validateLayerDocument } from "../layers/layerValidation";
import type { LayerRole, PersistedLayerState } from "../layers/layerTypes";

export interface ProjectMigrationResult {
  project: PerfectDrawProject;
  migrated: boolean;
}

function deterministicId(projectId: string, label: string): string {
  return `${projectId}-${label}`;
}

function normalizeLegacyPanels(
  project: PerfectDrawProject,
  templateId: ComicTemplateId,
): ComicPanel[] {
  const rawPanels = Array.isArray(project.panels) ? project.panels : [];
  const panels = rawPanels.map((panel, index) => ({
    ...panel,
    templateSlot: typeof panel.templateSlot === "number" ? panel.templateSlot : index,
    order: typeof panel.order === "number" ? panel.order : index,
  }));
  if (panels.length > 0 && isPanelGeometryValid(panels, project.width, project.height)) {
    return panels;
  }
  return createPanelTemplate(
    templateId,
    project.width,
    project.height,
    project.orientation,
    (slot) => deterministicId(project.id, `panel-${slot + 1}`),
  );
}

function inferRasterRole(layer: Partial<PerfectDrawLayer>): Exclude<LayerRole, "dialogue"> {
  if (layer.role && layer.role !== "dialogue") return layer.role;
  const name = typeof layer.name === "string" ? layer.name.toLocaleLowerCase() : "";
  if (name.startsWith("sketch")) return "sketch";
  if (name.startsWith("ink")) return "ink";
  if (name.startsWith("colour") || name.startsWith("color")) return "colour";
  if (name.startsWith("background")) return "background";
  return "custom";
}

function normalizeRasterLayers(project: PerfectDrawProject): PersistedLayerState[] {
  const source = Array.isArray(project.layers) ? project.layers : [];
  const raster = source
    .filter((layer) => layer?.type === "raster")
    .slice(0, 5)
    .map((layer, index): PersistedLayerState => {
      const role = inferRasterRole(layer);
      return {
        id:
          typeof layer.id === "string" && layer.id
            ? layer.id
            : deterministicId(project.id, `raster-${index + 1}`),
        name: sanitizeLayerName(layer.name) || defaultLayerName(role),
        type: "raster",
        role,
        system: false,
        visible: typeof layer.visible === "boolean" ? layer.visible : true,
        locked: typeof layer.locked === "boolean" ? layer.locked : false,
        opacity:
          typeof layer.opacity === "number" && Number.isFinite(layer.opacity)
            ? Math.min(1, Math.max(0, layer.opacity))
            : 1,
        order: index,
      };
    });
  if (raster.length > 0) return raster;
  return [
    {
      id: deterministicId(project.id, "sketch"),
      name: "Sketch",
      type: "raster",
      role: "sketch",
      system: false,
      visible: true,
      locked: false,
      opacity: 1,
      order: 0,
    },
  ];
}

function ensureDialogueLayer(
  project: PerfectDrawProject,
  rasterLayers: PersistedLayerState[],
): {
  layers: PersistedLayerState[];
  dialogueLayerId?: string;
} {
  if (project.type !== "comic") return { layers: normalizeLayerOrder(rasterLayers) };
  const existing = project.layers?.find((layer) => layer.type === "object");
  const layer: PersistedLayerState = {
    id: existing?.id || deterministicId(project.id, "dialogue"),
    name: sanitizeLayerName(existing?.name) || "Dialogue",
    type: "object",
    role: "dialogue",
    system: true,
    visible: typeof existing?.visible === "boolean" ? existing.visible : true,
    locked: typeof existing?.locked === "boolean" ? existing.locked : false,
    opacity:
      typeof existing?.opacity === "number" && Number.isFinite(existing.opacity)
        ? Math.min(1, Math.max(0, existing.opacity))
        : 1,
    order: existing && Number.isInteger(existing.order) ? existing.order : rasterLayers.length,
  };
  const ordered = normalizeLayerOrder([...rasterLayers, layer]);
  return { layers: ordered, dialogueLayerId: layer.id };
}

function migrateLayerStack(project: PerfectDrawProject): {
  layers: PersistedLayerState[];
  dialogueLayerId?: string;
  activeLayerId: string;
} {
  if (project.schemaVersion === PROJECT_SCHEMA_VERSION) {
    try {
      const validated = validateLayerDocument(project.layers, project.activeLayerId, project.type);
      return {
        layers: validated.layers,
        dialogueLayerId: validated.dialogueLayerId,
        activeLayerId: validated.activeLayerId,
      };
    } catch {
      // Repair malformed current-version metadata with the same safe migration
      // path used for older local records; no artwork record is deleted.
    }
  }
  const rasterLayers = normalizeRasterLayers(project);
  const { layers, dialogueLayerId } = ensureDialogueLayer(project, rasterLayers);
  const activeLayerId = layers.some((layer) => layer.id === project.activeLayerId)
    ? project.activeLayerId
    : rasterLayers[0]!.id;
  return { layers, dialogueLayerId, activeLayerId };
}

/*
 * Migration remains pure and deliberately leaves legacy artwork records alone;
 * recovery maps their single PNG to the migrated Sketch layer.
 */
function migrationChanged(
  project: PerfectDrawProject,
  layers: ReadonlyArray<PersistedLayerState>,
  activeLayerId: string,
): boolean {
  return (
    project.schemaVersion !== PROJECT_SCHEMA_VERSION ||
    project.activeLayerId !== activeLayerId ||
    !Array.isArray(project.layers) ||
    project.layers.length !== layers.length ||
    project.layers.some((layer, index) => {
      const migrated = layers[index];
      return (
        !migrated ||
        JSON.stringify({ ...layer, bitmapBlob: undefined }) !== JSON.stringify(migrated)
      );
    })
  );
}

function normalizeLegacyObjects(
  objects: ReadonlyArray<ComicBubbleObject>,
  panels: ReadonlyArray<ComicPanel>,
  dialogueLayerId: string | undefined,
): ComicBubbleObject[] {
  if (!dialogueLayerId) return [];
  const panelIds = new Set(panels.map((panel) => panel.id));
  return objects
    .filter(
      (object) =>
        object &&
        typeof object === "object" &&
        panelIds.has(object.panelId) &&
        (object.type === "speechBubble" ||
          object.type === "thoughtBubble" ||
          object.type === "shoutBubble" ||
          object.type === "narrationBox"),
    )
    .map((object) => ({ ...object, layerId: dialogueLayerId, data: { ...object.data } }));
}

export function migrateProject(project: PerfectDrawProject): ProjectMigrationResult {
  if (project.schemaVersion > PROJECT_SCHEMA_VERSION) return { project, migrated: false };
  const { layers, dialogueLayerId, activeLayerId } = migrateLayerStack(project);
  if (project.type === "drawing") {
    const migrated =
      migrationChanged(project, layers, activeLayerId) ||
      project.comicTemplateId !== undefined ||
      (project.panels?.length ?? 0) > 0 ||
      (project.objects?.length ?? 0) > 0;
    return {
      project: {
        ...project,
        schemaVersion: PROJECT_SCHEMA_VERSION,
        comicTemplateId: undefined,
        activeLayerId,
        layers: layers.map((layer) => ({ ...layer })),
        panels: [],
        objects: [],
      },
      migrated,
    };
  }

  const templateId = isComicTemplateId(project.comicTemplateId)
    ? project.comicTemplateId
    : inferTemplateIdFromPanelCount(project.panels?.length ?? 0);
  const panels = normalizeLegacyPanels(project, templateId);
  const objects = normalizeLegacyObjects(project.objects ?? [], panels, dialogueLayerId);
  const migratedProject: PerfectDrawProject = {
    ...project,
    schemaVersion: PROJECT_SCHEMA_VERSION,
    comicTemplateId: templateId,
    layers,
    activeLayerId,
    panels,
    objects,
  };
  return {
    project: migratedProject,
    migrated:
      migrationChanged(project, layers, activeLayerId) ||
      project.comicTemplateId !== templateId ||
      project.panels.length !== panels.length,
  };
}

export function projectDocumentState(project: PerfectDrawProject): PersistedDocumentState {
  const migrated = migrateProject(project).project;
  return validatePersistedDocumentState(
    {
      schemaVersion: COMIC_DOCUMENT_SCHEMA_VERSION,
      activeLayerId: migrated.activeLayerId,
      layers: migrated.layers.map((layer) => ({ ...layer })),
      comicTemplateId: migrated.comicTemplateId,
      panels: migrated.panels,
      objects: migrated.objects,
    },
    migrated,
  );
}

export function applyDocumentStateToProject(
  project: PerfectDrawProject,
  state: PersistedDocumentState,
): PerfectDrawProject {
  const validated = validatePersistedDocumentState(state, project);
  return {
    ...project,
    schemaVersion: PROJECT_SCHEMA_VERSION,
    comicTemplateId: validated.comicTemplateId,
    activeLayerId: validated.activeLayerId,
    layers: validated.layers.map((layer) => ({ ...layer })),
    panels: validated.panels.map((panel) => ({ ...panel })),
    objects: validated.objects.map((object) => ({ ...object, data: { ...object.data } })),
  };
}

export function cloneProjectDocumentState(project: PerfectDrawProject): PersistedDocumentState {
  return cloneDocumentState(projectDocumentState(project));
}

/** Migrates the paired Milestone 4 document state without touching its artwork. */
export function migratePersistedDocumentState(
  value: unknown,
  project: PerfectDrawProject,
): PersistedDocumentState {
  const migratedProject = migrateProject(project).project;
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (record.schemaVersion === COMIC_DOCUMENT_SCHEMA_VERSION) {
      return validatePersistedDocumentState(value, migratedProject);
    }
    if (
      record.schemaVersion === 1 &&
      Array.isArray(record.panels) &&
      Array.isArray(record.objects)
    ) {
      const dialogueLayerId = migratedProject.layers.find((layer) => layer.type === "object")?.id;
      return validatePersistedDocumentState(
        {
          schemaVersion: COMIC_DOCUMENT_SCHEMA_VERSION,
          activeLayerId: migratedProject.activeLayerId,
          layers: migratedProject.layers.map((layer) => ({ ...layer })),
          comicTemplateId: record.comicTemplateId,
          panels: record.panels,
          objects: dialogueLayerId
            ? (record.objects as ComicBubbleObject[]).map((object) => ({
                ...object,
                layerId: dialogueLayerId,
              }))
            : [],
        },
        migratedProject,
      );
    }
  }
  throw new Error("Document schema is not supported.");
}
